// RECRUITMENT-CANDIDATE-PROFILE-EDIT-UI-004 — in-drawer Edit Mode (presentation only; same form state / payload / validation as before)
import React from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { QUALIFICATIONS, TriSelect, ExpInput, CtcInput } from '../CandidateProfileFields';
import { Card } from './drawerShared';

const Field = ({ label, hint, children, testId }) => (
  <div className="min-w-0 space-y-1.5" data-testid={testId}>
    <Label className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-500">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

const Grid = ({ children }) => <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">{children}</div>;

const ActionBar = ({ saving, onCancel, onSave, idPrefix }) => (
  <div className="flex items-center gap-2">
    <Button variant="outline" size="sm" className="h-9 px-4 text-xs font-semibold" onClick={onCancel} disabled={saving} data-testid={`${idPrefix}-cancel-btn`}>Cancel</Button>
    <Button size="sm" className="h-9 px-4 text-xs font-semibold" onClick={onSave} disabled={saving} data-testid={`${idPrefix}-save-btn`}>{saving ? 'Saving…' : 'Save Changes'}</Button>
  </div>
);

export const EditProfilePanel = ({ value, onChange, showCtc, saving, onCancel, onSave }) => {
  const set = (k, v) => onChange({ ...value, [k]: k === 'highest_qualification' && v === 'none' ? '' : v });
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="edit-profile-panel">
      <div className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-6 py-3" data-testid="edit-profile-toolbar">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700"><Pencil className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="sf-heading text-sm font-semibold tracking-tight text-slate-900" data-testid="edit-profile-title">Editing Candidate Profile</p>
            <p className="truncate text-xs text-slate-500">Changes are saved only when you click Save Changes.</p>
          </div>
        </div>
        <ActionBar saving={saving} onCancel={onCancel} onSave={onSave} idPrefix="edit-profile" />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
        <div className="space-y-5" data-testid="candidate-profile-fields">
          <Card title="Personal & Location" data-testid="edit-section-personal">
            <Grid>
              <Field label="Current Location">
                <Input className="h-9" placeholder="City, State" value={value.current_location} onChange={(e) => set('current_location', e.target.value)} data-testid="profile-current-location" />
              </Field>
              <Field label="Preferred Location">
                <Input className="h-9" placeholder="City, State" value={value.preferred_location} onChange={(e) => set('preferred_location', e.target.value)} data-testid="profile-preferred-location" />
              </Field>
              <Field label="Willing to Relocate">
                <TriSelect value={value.willing_to_relocate} onChange={(v) => set('willing_to_relocate', v)} testId="profile-willing-to-relocate" />
              </Field>
            </Grid>
          </Card>

          <Card title="Education & Experience" data-testid="edit-section-education">
            <Grid>
              <Field label="Highest Qualification">
                <Select value={value.highest_qualification || 'none'} onValueChange={(v) => set('highest_qualification', v)}>
                  <SelectTrigger className="h-9" data-testid="profile-highest-qualification"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    {QUALIFICATIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Qualification Detail">
                <Input className="h-9" placeholder="e.g. B.Com, MBA (Finance)" value={value.qualification_detail} onChange={(e) => set('qualification_detail', e.target.value)} data-testid="profile-qualification-detail" />
              </Field>
              <Field label="Relevant Work Experience" hint="Years and months. Cannot exceed total experience.">
                <ExpInput value={value.relevant_experience} onChange={(v) => set('relevant_experience', v)} testId="profile-relevant-exp" />
              </Field>
              <Field label="Total Work Experience" hint="Years and months.">
                <ExpInput value={value.total_experience} onChange={(v) => set('total_experience', v)} testId="profile-total-exp" />
              </Field>
            </Grid>
          </Card>

          <Card title={showCtc ? 'Compensation & Availability' : 'Availability'} data-testid="edit-section-compensation">
            <Grid>
              <Field label="Notice Period" hint="Days. Enter 0 for immediate availability.">
                <Input type="number" min={0} max={365} className="h-9" placeholder="e.g. 30" value={value.notice_period_days} onChange={(e) => set('notice_period_days', e.target.value)} data-testid="profile-notice-period" />
              </Field>
              <Field label="Notice Period Negotiable">
                <TriSelect value={value.notice_negotiable} onChange={(v) => set('notice_negotiable', v)} testId="profile-notice-negotiable" />
              </Field>
              {showCtc && (
                <>
                  <Field label="Current CTC" hint="Annual amount.">
                    <CtcInput value={value.current_ctc} onChange={(v) => set('current_ctc', v)} testId="profile-current-ctc" />
                  </Field>
                  <Field label="Expected CTC" hint="Annual amount.">
                    <CtcInput value={value.expected_ctc} onChange={(v) => set('expected_ctc', v)} testId="profile-expected-ctc" />
                  </Field>
                  <Field label="CTC Negotiable">
                    <TriSelect value={value.ctc_negotiable} onChange={(v) => set('ctc_negotiable', v)} testId="profile-ctc-negotiable" />
                  </Field>
                </>
              )}
            </Grid>
          </Card>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-4 border-t border-slate-200 bg-white/95 px-6 py-3 backdrop-blur" data-testid="edit-profile-footer">
        <p className="text-xs text-slate-500">Cancel discards unsaved changes.</p>
        <ActionBar saving={saving} onCancel={onCancel} onSave={onSave} idPrefix="edit-profile-footer" />
      </div>
    </div>
  );
};
